import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowUpRight,
  Truck, 
  Globe2, 
  ShieldCheck, 
  CheckCircle2,
  Star,
  Zap,
  MapPin,
  Container,
  Anchor,
  Thermometer,
  Box,
  FileCheck,
  ArrowRight
} from 'lucide-react';

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, target]);

  return (
    <span ref={ref} className="font-mono font-bold">
      {count}{suffix}
    </span>
  );
};

const Home = () => {
  const services = [
    { icon: Truck, title: 'Full Truckload', desc: 'Dedicated capacity for your largest shipments', href: '/ground' },
    { icon: Box, title: 'LTL Solutions', desc: 'Cost-effective consolidation and distribution', href: '/ground' },
    { icon: Thermometer, title: 'Temp Control', desc: 'Climate-controlled transport for sensitive cargo', href: '/equipment' },
    { icon: Container, title: 'Heavy Haul', desc: 'Specialized equipment for oversized loads', href: '/equipment' },
    { icon: FileCheck, title: 'Customs Brokerage', desc: 'Seamless cross-border clearance', href: '/cross-border' },
    { icon: Anchor, title: 'Global Forwarding', desc: 'International air and ocean freight', href: '/global-tech' },
  ];

  const testimonials = [
    { 
      initials: 'JD', 
      name: 'John Davidson', 
      role: 'Supply Chain Director', 
      company: 'Maple Leaf Foods',
      text: 'Quinte transformed our cross-border operations. Their customs expertise eliminated delays we had struggled with for years.'
    },
    { 
      initials: 'SM', 
      name: 'Sarah Mitchell', 
      role: 'Logistics Manager', 
      company: 'Canadian Tire',
      text: 'Asset-backed reliability means our seasonal peaks are never a problem. 99.8% on-time performance speaks for itself.'
    },
    { 
      initials: 'MC', 
      name: 'Michael Chen', 
      role: 'VP Operations', 
      company: 'TechFlow Distribution',
      text: 'The technology platform gives us visibility we never had before. Real-time tracking and automated documentation saves hours daily.'
    },
  ];

  const capabilities = [
    { title: 'Asset-Backed', desc: '40 tractors, 50 trailers owned and operated', icon: Truck },
    { title: 'End-to-End', desc: 'From pickup to delivery, full visibility', icon: MapPin },
    { title: 'Customs-Native', desc: 'In-house CBSA and CBP expertise', icon: FileCheck },
    { title: 'Tech-Enabled', desc: 'Real-time tracking and automated docs', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-bone">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-bone">
        <div className="absolute inset-0 bg-mesh" />
        
        <motion.div 
          className="absolute top-32 right-8 lg:right-16 w-32 h-32 lg:w-48 lg:h-48"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-full h-full bg-amber/20 rounded-full flex items-center justify-center">
            <span className="font-display font-black text-6xl lg:text-8xl text-amber">Q</span>
          </div>
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4"
              >
                Canadian Asset-Backed Brokerage
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="font-display font-black text-display-lg text-forest mb-6"
              >
                <span className="block">Engineering{' '}
                  <span className="relative inline-block">
                    Unrelenting
                    <span className="absolute bottom-2 left-0 w-full h-3 bg-amber -z-10 transform -skew-x-6" />
                  </span>
                </span>
                <span className="block">Capacity Across North America.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-body text-lg text-ink/70 mb-6 leading-relaxed"
              >
                End-to-end freight brokerage and cross-border logistics engineered for seamless 
                supply chains. Headquartered in Belleville, Ontario — moving your freight 
                frictionlessly across Canada and the USA.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <a href="/quote" className="btn-primary group">
                  Get a Quote
                  <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a href="/ground" className="btn-ghost">
                  Explore Services
                </a>
              </motion.div>
            </div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden lg:block relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=800&auto=format&fit=crop&q=80" 
                  alt="USA American semi truck on highway"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/30 to-transparent" />
              </div>
              {/* Floating badge */}
              <motion.div 
                className="absolute -bottom-4 -left-4 bg-forest text-white p-4 rounded-xl shadow-lg"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="font-display font-bold text-2xl">50+</div>
                <div className="font-mono text-xs text-white/70">Trailers</div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-forest/30 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-amber rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* STATS BAR */}
      <section className="bg-forest py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-dots" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: 40, label: 'Tractors' },
              { value: 50, label: 'Trailers' },
              { value: 2, label: 'Nations' },
              { value: 1, label: 'Partner', isText: true },
            ].map((stat, i) => (
              <motion.div 
                key={i}
                className="group"
                whileHover={{ scale: 1.05 }}
              >
                <div className="font-mono text-5xl lg:text-6xl font-bold text-amber mb-2">
                  {stat.isText ? '1' : <AnimatedCounter target={stat.value} />}
                </div>
                <div className="font-body text-white/60 text-sm uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUINTE ADVANTAGE */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Capability Boxes - Now on Left */}
            <div className="grid sm:grid-cols-2 gap-4 order-2 lg:order-1">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-forest/10 hover:border-amber/50 transition-colors group"
                >
                  <div className="w-12 h-12 bg-forest rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber transition-colors">
                    <cap.icon className="w-6 h-6 text-white group-hover:text-forest" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-forest mb-2">{cap.title}</h3>
                  <p className="font-body text-ink/60 text-sm">{cap.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Text Content - Now on Right */}
            <div className="order-1 lg:order-2">
              <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
                The Quinte Advantage
              </div>
              <h2 className="font-display font-black text-display-lg text-forest mb-6">
                Why Shippers Choose Quinte
              </h2>
              <p className="font-body text-ink/70 text-lg leading-relaxed mb-6">
                We are not a load board. We are not a broker-aggregator. We are an asset-backed 
                freight brokerage with owned equipment, hired drivers, and in-house customs expertise.
              </p>
              <p className="font-body text-ink/70 text-lg leading-relaxed">
                When you book with Quinte, you book capacity that exists — not capacity we hope to find. 
                That is the difference between a broker and a partner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
              Capabilities
            </div>
            <h2 className="font-display font-black text-display-lg text-forest">
              Comprehensive Logistics Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.a
                key={i}
                href={service.href}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative bg-bone p-8 rounded-2xl border border-forest/10 hover:border-amber hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-amber/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-amber transition-all duration-500 group-hover:w-full" />
                <div className="relative z-10">
                  <motion.div 
                    className="w-14 h-14 bg-forest rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber transition-colors duration-300"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <service.icon className="w-7 h-7 text-white group-hover:text-forest transition-colors duration-300" />
                  </motion.div>
                  <h3 className="font-display font-bold text-xl text-forest mb-2 flex items-center gap-2">
                    {service.title}
                    <ArrowUpRight className="w-5 h-5 opacity-0 -translate-x-2 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
                  </h3>
                  <p className="font-body text-ink/60">{service.desc}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding bg-forest relative overflow-hidden">
        <div className="absolute inset-0 bg-dots" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-amber mb-4">
              Client Success
            </div>
            <h2 className="font-display font-black text-display-lg text-white">
              Trusted by Industry Leaders
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-forest-2 p-8 rounded-2xl hover:bg-forest-3 transition-all duration-300 cursor-pointer"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <motion.div
                      key={j}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 + j * 0.1 }}
                    >
                      <Star className="w-5 h-5 text-amber fill-amber" />
                    </motion.div>
                  ))}
                </div>
                <p className="font-body text-white/80 mb-6 leading-relaxed group-hover:text-white transition-colors duration-300">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 bg-amber rounded-full flex items-center justify-center font-display font-bold text-forest"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    {testimonial.initials}
                  </motion.div>
                  <div>
                    <div className="font-display font-bold text-white">{testimonial.name}</div>
                    <div className="font-body text-white/60 text-sm">{testimonial.role}</div>
                    <div className="font-mono text-amber text-xs">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10 text-center">
          <h2 className="font-display font-black text-display-lg text-forest mb-6">
            Move freight without friction.
          </h2>
          <p className="font-body text-ink/70 text-xl max-w-2xl mx-auto mb-10">
            Join hundreds of businesses that trust Quinte Freight for their logistics needs. 
            Get your quote in minutes, not days.
          </p>
          <a href="/quote" className="btn-primary text-lg px-10 py-5">
            Get Your Quote Now
            <ArrowRight className="w-6 h-6" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
